import React, { useState, useEffect } from 'react';
import useMeetingProtocol from '../../hooks/useMeetingProtocol';
import { useNotifications } from '../../../../contexts/NotificationContext';
import { useAuth } from '../../../../contexts/AuthContext';
import api from '../../../../services/api';
import ProtocolRecipientModal from './ProtocolRecipientModal';
import { MEETING_TEMPLATES } from '../../utils/meetingTemplates';

// Inline SVG icons
// Mötesnamn till protokolltiteln. Läser etiketten ur MEETING_TEMPLATES, som är
// frontendens kanoniska lista över mötestyper — tidigare låg en egen kopia här
// som saknade hemlaxa och uppfoljning_underlag och döpte dem till "Möte".
// Nya mötestyper får nu rätt titel utan att den här filen rörs.
//
// Ligger på modulnivå och tar meeting som argument, så att effekten som sätter
// titeln kan ha meeting som sitt enda beroende.
const getMeetingName = (meeting) => {
  const label = MEETING_TEMPLATES[meeting?.type]?.label || 'Möte';
  if (meeting?.type === 'byggmote' && meeting?.meetingNumber) {
    return `${label} #${meeting.meetingNumber}`;
  }
  return label;
};

const XMarkIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const DocumentTextIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const PlusIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
  </svg>
);

const TrashIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const ChevronUpIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
  </svg>
);

const ChevronDownIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
  </svg>
);

const CheckCircleIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const PaperAirplaneIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

const ClipboardCheckIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

const UserIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);


const CalendarIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

/**
 * Komponent för att bygga mötesprotokoll
 *
 * @param {Object} props
 * @param {string} props.meetingId - ID för mötet
 * @param {Object} props.meeting - Mötesobjekt med deltagare
 * @param {function} props.onClose - Callback för att stänga
 */
export function ProtocolBuilder({ meetingId, meeting, onClose }) {
  const {
    protocol,
    canWrite,
    loading,
    createProtocol,
    updateProtocol,
    markAsSent,
    deleteProtocol,
    toggleActionPoint: hookToggleActionPoint
  } = useMeetingProtocol(meetingId);

  const { user } = useAuth();

  // State för utkast
  const [title, setTitle] = useState('');
  const [introduction, setIntroduction] = useState('');
  const [sections, setSections] = useState([]);
  const [conclusion, setConclusion] = useState('');
  const [presentAttendees, setPresentAttendees] = useState([]);

  // State för anteckningar att inkludera
  const [availableNotes, setAvailableNotes] = useState([]);
  const [selectedNoteIds, setSelectedNoteIds] = useState([]);
  const [, setLoadingNotes] = useState(false);

  // State för alla projektmedlemmar (för att kunna tilldela actionpoints)
  const [projectMembers, setProjectMembers] = useState([]);
  const [showRecipientModal, setShowRecipientModal] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const { showConfirm, showError, showSuccess } = useNotifications();

  // Översätt roll till svenska för visning
  const getRoleLabel = (role) => {
    const roleLabels = {
      'customer': 'Kund',
      'kund': 'Kund',
      'consultant': 'Byggkonsult',
      'byggkonsult': 'Byggkonsult',
      'builder': 'Byggare',
      'byggare': 'Byggare',
      'architect': 'Inredningsarkitekt',
      'inredningsarkitekt': 'Inredningsarkitekt',
      'admin': 'Admin',
      'UE': 'Underentreprenör'
    };
    return roleLabels[role] || role;
  };

  // Mappa engelska roller till svenska för backend
  const mapRoleToSwedish = (role) => {
    const roleMap = {
      'customer': 'kund',
      'consultant': 'byggkonsult',
      'builder': 'byggare',
      'architect': 'inredningsarkitekt'
    };
    return roleMap[role] || role;
  };

  // Ladda befintligt protokoll eller skapa default
  useEffect(() => {
    if (protocol) {
      setTitle(protocol.title || '');
      setIntroduction(protocol.introduction || '');
      setSections(protocol.sections || []);
      setConclusion(protocol.conclusion || '');
      setPresentAttendees(protocol.presentAttendees || []);
    } else if (meeting) {
      // Default värden
      const meetingDate = meeting.scheduledAt
        ? new Date(meeting.scheduledAt).toLocaleDateString('sv-SE')
        : '';
      setTitle(`Protokoll - ${getMeetingName(meeting)} ${meetingDate}`);
      setIntroduction('');
      setSections([]);
      setConclusion('');

      // Default deltagare från mötet
      const defaultAttendees = (meeting.attendees || []).map(a => ({
        userId: a.userId,
        name: a.name,
        role: a.role,
        present: true
      }));
      setPresentAttendees(defaultAttendees);
    }
  }, [protocol, meeting]);

  // Ladda delade anteckningar
  useEffect(() => {
    const fetchNotes = async () => {
      if (!meetingId) return;

      try {
        setLoadingNotes(true);
        const response = await api.get(`/meeting-notes/meeting/${meetingId}/for-protocol`);
        setAvailableNotes(response.data.data || []);
      } catch (err) {
        console.error('Fel vid hämtning av anteckningar:', err);
      } finally {
        setLoadingNotes(false);
      }
    };

    fetchNotes();
  }, [meetingId]);

  // Hämta alla projektmedlemmar för att kunna tilldela actionpoints
  useEffect(() => {
    const fetchProjectMembers = async () => {
      if (!meeting?.project && !meeting?.projectId) return;

      try {
        const projectId = meeting.project?._id || meeting.project || meeting.projectId?._id || meeting.projectId;
        const response = await api.get(`/projects/${projectId}`);
        const project = response.data.project || response.data;

        // Samla alla projektmedlemmar
        const members = [];

        // Kund
        if (project.customer) {
          const c = project.customer;
          members.push({
            userId: c._id,
            name: `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || 'Kund',
            role: 'kund'
          });
        }

        // Ytterligare kunder
        (project.additionalCustomers || []).forEach(c => {
          if (c) {
            members.push({
              userId: c._id,
              name: `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || 'Kund',
              role: 'kund'
            });
          }
        });

        // Byggkonsult
        if (project.consultant) {
          const c = project.consultant;
          members.push({
            userId: c._id,
            name: `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Byggkonsult',
            role: 'byggkonsult'
          });
        }

        // Ytterligare byggkonsulter
        (project.additionalConsultants || []).forEach(c => {
          if (c) {
            members.push({
              userId: c._id,
              name: `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Byggkonsult',
              role: 'byggkonsult'
            });
          }
        });

        // Inredningsarkitekt
        if (project.architect) {
          const a = project.architect;
          members.push({
            userId: a._id,
            name: `${a.firstName || ''} ${a.lastName || ''}`.trim() || 'Inredningsarkitekt',
            role: 'inredningsarkitekt'
          });
        }

        // Ytterligare arkitekter
        (project.additionalArchitects || []).forEach(a => {
          if (a) {
            members.push({
              userId: a._id,
              name: `${a.firstName || ''} ${a.lastName || ''}`.trim() || 'Inredningsarkitekt',
              role: 'inredningsarkitekt'
            });
          }
        });

        // Byggare (kontaktperson)
        if (project.builderContactPerson) {
          const b = project.builderContactPerson;
          members.push({
            userId: b._id,
            name: `${b.firstName || ''} ${b.lastName || ''}`.trim() || 'Byggare',
            role: 'byggare'
          });
        }

        // Underleverantörer (UE)
        (project.subcontractors || []).forEach(s => {
          if (s && s._id) {
            members.push({
              userId: s._id,
              name: `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Underleverantör',
              role: 'UE'
            });
          }
        });

        // Projektörer
        (project.projectors || []).forEach(p => {
          if (p && p._id) {
            members.push({
              userId: p._id,
              name: `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Projektör',
              role: 'projektor'
            });
          }
        });

        // Lägg till ALLA kontaktpersoner på byggföretaget (inte bara projektets ansvarige)
        const builderId = project.builder?._id || project.builder;
        if (builderId) {
          try {
            const bRes = await api.get(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/builders/${builderId}`);
            const builder = bRes.data;
            const addBuilder = (u) => {
              if (u && u._id && !members.some(m => String(m.userId) === String(u._id))) {
                members.push({
                  userId: u._id,
                  name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Byggare',
                  role: 'byggare'
                });
              }
            };
            addBuilder(builder.contactPerson);
            (builder.additionalContactPersons || []).forEach(addBuilder);
          } catch (_) { /* byggföretag kunde inte hämtas */ }
        }

        setProjectMembers(members);
      } catch (err) {
        console.error('Fel vid hämtning av projektmedlemmar:', err);
      }
    };

    fetchProjectMembers();
    // Både project och projectId: effekten läser båda (project först), så ett
    // möte som bara har project hämtade tidigare aldrig om när det ändrades.
  }, [meeting?.project, meeting?.projectId]);

  // Lägg till sektion från anteckning
  const addSectionFromNote = (note) => {
    // Om anteckningen är markerad som actionpoint, sätt skaparen som ansvarig
    let assignee = null;
    if (note.isActionPoint && note.createdBy) {
      assignee = {
        userId: note.createdBy._id,
        name: `${note.createdBy.firstName || ''} ${note.createdBy.lastName || ''}`.trim() || 'Okänd',
        role: note.createdBy.role
      };
    }

    const newSection = {
      _id: `temp_${Date.now()}`,
      title: '',
      content: note.content,
      room: note.room || '',
      order: sections.length,
      sourceNoteId: note._id,
      isActionPoint: note.isActionPoint || false,
      assignee
    };
    setSections([...sections, newSection]);
    setSelectedNoteIds([...selectedNoteIds, note._id]);
  };

  // Lägg till tom sektion (överst)
  const addEmptySection = () => {
    const newSection = {
      _id: `temp_${Date.now()}`,
      title: '',
      content: '',
      room: '',
      order: -1,
      isActionPoint: false,
      assignee: null,
      completed: false
    };
    setSections([newSection, ...sections]);
  };

  // Toggla actionpunkt
  const toggleActionPoint = (sectionId) => {
    setSections(sections.map(s => {
      if (s._id !== sectionId) return s;

      // Om vi stänger av actionpunkt, rensa assignee
      if (s.isActionPoint) {
        return { ...s, isActionPoint: false, assignee: null };
      }

      // Om vi aktiverar actionpunkt, sätt nuvarande användare som default-ansvarig
      const currentUserAsAssignee = {
        userId: user?._id,
        name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email,
        role: user?.role
      };

      return { ...s, isActionPoint: true, assignee: currentUserAsAssignee };
    }));
  };

  // Sätt ansvarig för actionpunkt
  const setAssignee = (sectionId, assignee) => {
    setSections(sections.map(s =>
      s._id === sectionId ? { ...s, assignee } : s
    ));
  };

  // Skapa unik identifierare för en deltagare
  const getAssigneeKey = (a) => {
    if (!a) return '';
    // Hantera MongoDB ObjectId (kan vara objekt med _id eller $oid)
    if (a.userId) {
      if (typeof a.userId === 'string') return a.userId;
      if (a.userId._id) return a.userId._id.toString();
      if (a.userId.$oid) return a.userId.$oid;
      if (typeof a.userId.toString === 'function') {
        const str = a.userId.toString();
        // Kontrollera att toString() inte gav [object Object]
        if (str && !str.includes('[object')) return str;
      }
    }
    // Fallback till namn + roll
    if (a.name && a.role) return `${a.name}-${a.role}`;
    if (a.name) return a.name;
    return '';
  };

  // Hämta tillgängliga ansvariga - alla projektmedlemmar (inte bara mötesdeltagare)
  const getAvailableAssignees = () => {
    // Börja med projektmedlemmar
    const assignees = [...projectMembers];

    // Lägg till mötesdeltagare som inte redan finns
    presentAttendees.forEach(a => {
      const exists = assignees.some(m =>
        m.userId?.toString() === a.userId?.toString() ||
        m.name === a.name
      );
      if (!exists && a.userId) {
        assignees.push({
          userId: a.userId,
          name: a.name,
          role: a.role
        });
      }
    });

    // Sortera efter roll (byggkonsult först, sedan inredningsarkitekt, byggare, kund)
    const roleOrder = { byggkonsult: 1, inredningsarkitekt: 2, byggare: 3, kund: 4 };
    return assignees.sort((a, b) => (roleOrder[a.role] || 5) - (roleOrder[b.role] || 5));
  };

  // Ta bort sektion
  const removeSection = (sectionId) => {
    const section = sections.find(s => s._id === sectionId);
    setSections(sections.filter(s => s._id !== sectionId));
    if (section?.sourceNoteId) {
      setSelectedNoteIds(selectedNoteIds.filter(id => id !== section.sourceNoteId));
    }
  };

  // Uppdatera sektion
  const updateSection = (sectionId, field, value) => {
    setSections(sections.map(s =>
      s._id === sectionId ? { ...s, [field]: value } : s
    ));
  };

  // Flytta sektion uppåt/nedåt
  const moveSection = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= sections.length) return;

    const newSections = [...sections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    // Uppdatera order
    newSections.forEach((s, i) => s.order = i);
    setSections(newSections);
  };

  // Toggle deltagare närvaro
  const toggleAttendeePresent = (index) => {
    setPresentAttendees(presentAttendees.map((a, i) =>
      i === index ? { ...a, present: !a.present } : a
    ));
  };

  // Spara protokoll
  const handleSave = async () => {
    if (!title.trim()) {
      setError('Titel krävs');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const data = {
        title,
        introduction,
        sections: sections.map((s, i) => ({
          ...s,
          order: i,
          // Mappa roller till svenska för backend
          assignee: s.assignee ? {
            ...s.assignee,
            role: mapRoleToSwedish(s.assignee.role)
          } : null
        })),
        conclusion,
        presentAttendees: presentAttendees.map(a => ({
          ...a,
          role: mapRoleToSwedish(a.role)
        }))
      };

      if (protocol) {
        await updateProtocol(protocol._id, data);
      } else {
        // Skicka INTE includeNoteIds - sektioner skapas redan i frontend när användaren väljer anteckningar
        await createProtocol(data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte spara protokoll');
    } finally {
      setSaving(false);
    }
  };

  // Publicera protokoll — öppna mottagarväljaren (förval = mötesdeltagare)
  const handleMarkSent = () => {
    if (!protocol?._id) return;
    setShowRecipientModal(true);
  };

  // Publicera med vald mottagarlista
  const handlePublishWithRecipients = async (recipients) => {
    if (!protocol?._id) return;
    try {
      setSaving(true);
      await markAsSent(protocol._id, recipients);
      setShowRecipientModal(false);
      showSuccess('Protokoll publicerat', 'Protokollet har publicerats och valda mottagare har fått notis.');
    } catch (err) {
      showError('Kunde inte publicera', err.response?.data?.message || 'Ett fel uppstod vid publicering av protokollet.');
    } finally {
      setSaving(false);
    }
  };

  // Radera protokoll
  const handleDelete = async () => {
    if (!protocol?._id) return;

    const confirmed = await showConfirm(
      'Ta bort protokoll',
      `Är du säker på att du vill ta bort protokollet "${protocol.title}"?\n\nDenna åtgärd kan inte ångras.`
    );

    if (!confirmed) return;

    try {
      setSaving(true);
      await deleteProtocol(protocol._id);
      showSuccess('Protokoll borttaget', 'Protokollet har tagits bort.');
      onClose?.();
    } catch (err) {
      showError('Kunde inte ta bort', err.response?.data?.message || 'Ett fel uppstod vid borttagning av protokollet.');
    } finally {
      setSaving(false);
    }
  };


  const isReadOnly = !canWrite || (protocol?.status && protocol.status !== 'draft');
  const statusLabel = {
    draft: 'Utkast',
    finalized: 'Utkast', // Bakåtkompatibilitet - behandlas som utkast
    sent: 'Publicerat'
  };

  // Stäng vid escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* Modal */}
      {/* dvh i stället för vh — iOS räknar vh utan webbläsarens verktygsfält,
          så 90vh kan sträcka sig utanför den synliga ytan och kapa rubriken
          och stängkrysset. Samma orsak som i Modal.css. */}
      <div className="relative w-full max-w-3xl mx-4 bg-white rounded-lg shadow-xl flex flex-col max-h-[90dvh]">
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center justify-between">
          {isReadOnly ? (
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
              {meeting?.scheduledAt && (
                <p className="text-sm text-gray-500 mt-1">
                  {new Date(meeting.scheduledAt).toLocaleDateString('sv-SE', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="w-5 h-5 text-leadme-pink" />
              <h3 className="font-semibold text-gray-900">Protokoll</h3>
              {protocol?.status && (
                <span className={`px-2 py-0.5 text-xs rounded-full
                  ${protocol.status === 'sent' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                  {statusLabel[protocol.status]}
                </span>
              )}
            </div>
          )}

          {/* Stäng-knapp */}
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors"
            title="Stäng"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

      {/* Error */}
      {error && (
        <div className="px-4 py-2 bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Content - Read-only clean view */}
      {isReadOnly ? (
        <div className="flex-1 overflow-y-auto p-6">
          {/* Närvarande */}
          {presentAttendees.filter(a => a.present).length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Närvarande</h3>
              <div className="flex flex-wrap gap-2">
                {presentAttendees.filter(a => a.present).map((attendee, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2 py-1 rounded bg-green-50 text-green-800 text-sm"
                  >
                    <CheckCircleIcon className="w-4 h-4 mr-1" />
                    {attendee.name} ({getRoleLabel(attendee.role)})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Inledning */}
          {introduction && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Inledning</h3>
              <p className="text-gray-600 whitespace-pre-wrap">{introduction}</p>
            </div>
          )}

          {/* Punkter — grupperade per rum */}
          {sections.filter(s => !s.isActionPoint).length > 0 && (() => {
            const nonAction = sections.filter(s => !s.isActionPoint);
            const byRoom = {};
            nonAction.forEach(s => {
              const key = s.room?.trim() || '__generellt__';
              if (!byRoom[key]) byRoom[key] = [];
              byRoom[key].push(s);
            });
            const roomKeys = Object.keys(byRoom).sort((a, b) => {
              if (a === '__generellt__') return 1;
              if (b === '__generellt__') return -1;
              return a.localeCompare(b, 'sv');
            });

            return (
              <div className="mb-6 space-y-5">
                {roomKeys.map(roomKey => (
                  <div key={roomKey}>
                    <h3 className="text-sm font-semibold text-gray-800 mb-2">
                      {roomKey === '__generellt__' ? 'Generella punkter' : roomKey}
                    </h3>
                    <div className="space-y-3">
                      {byRoom[roomKey].map((section, idx) => (
                        <div
                          key={section._id || idx}
                          className="border-l-2 border-leadme-pink pl-4"
                        >
                          {section.title && (
                            <h4 className="font-medium text-gray-900">{section.title}</h4>
                          )}
                          {section.content && (
                            <p className={`${section.title ? 'mt-1' : ''} text-gray-600 whitespace-pre-wrap`}>{section.content}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Actionpunkter */}
          {sections.filter(s => s.isActionPoint).length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-leadme-pink mb-3 flex items-center gap-1">
                <ClipboardCheckIcon className="w-4 h-4" />
                Actionpunkter
              </h3>
              <div className="space-y-3">
                {sections.filter(s => s.isActionPoint).map((section, idx) => (
                  <div
                    key={section._id || idx}
                    className={`border rounded-lg p-3 ${
                      section.completed
                        ? 'border-green-300 bg-green-50/30'
                        : 'border-leadme-pink bg-pink-50/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={section.completed || false}
                        onChange={() => {
                          if (protocol?._id) {
                            hookToggleActionPoint(protocol._id, section._id, !section.completed);
                          }
                        }}
                        disabled={user?.role !== 'admin' && section.assignee?.userId?.toString() !== user?._id?.toString() && section.assignee?.userId !== user?._id}
                        className={`mt-1 rounded text-leadme-pink focus:ring-leadme-pink ${
                          user?.role !== 'admin' && section.assignee?.userId?.toString() !== user?._id?.toString() && section.assignee?.userId !== user?._id
                            ? 'cursor-not-allowed opacity-50'
                            : 'cursor-pointer'
                        }`}
                        title={user?.role !== 'admin' && section.assignee?.userId?.toString() !== user?._id?.toString() && section.assignee?.userId !== user?._id
                          ? 'Endast den ansvariga kan markera denna punkt'
                          : ''}
                      />
                      <div className="flex-1">
                        <h4 className={`font-medium ${section.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                          {section.title}
                        </h4>
                        {section.assignee && (
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <UserIcon className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-sm text-gray-600">{section.assignee.name}</span>
                            <span className="text-xs text-leadme-pink bg-leadme-pink/10 px-2 py-0.5 rounded">
                              {getRoleLabel(section.assignee.role)}
                            </span>
                          </div>
                        )}
                        {section.content && (
                          <p className={`mt-1.5 text-sm whitespace-pre-wrap ${section.completed ? 'text-gray-400' : 'text-gray-600'}`}>
                            {section.content}
                          </p>
                        )}
                        {section.dueDate && !section.completed && (
                          <span className={`inline-flex items-center gap-1 mt-1.5 text-xs px-2 py-0.5 rounded ${
                            new Date(section.dueDate) < new Date()
                              ? 'text-red-600 bg-red-100'
                              : 'text-orange-600 bg-orange-100'
                          }`}>
                            <CalendarIcon className="w-3 h-3" />
                            Deadline: {new Date(section.dueDate).toLocaleDateString('sv-SE')}
                          </span>
                        )}
                        {section.completed && (
                          <span className="inline-flex items-center gap-1 mt-1.5 text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded">
                            <CheckCircleIcon className="w-3 h-3" />
                            Avklarad {section.completedAt && new Date(section.completedAt).toLocaleDateString('sv-SE')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Avslutning */}
          {conclusion && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Avslutning</h3>
              <p className="text-gray-600 whitespace-pre-wrap">{conclusion}</p>
            </div>
          )}

          {/* Metadata */}
          <div className="pt-4 border-t text-xs text-gray-400">
            {protocol?.createdBy && (
              <p>Skapad av: {protocol.createdBy.firstName} {protocol.createdBy.lastName}</p>
            )}
            {protocol?.sentAt && (
              <p>Publicerad: {new Date(protocol.sentAt).toLocaleString('sv-SE')}</p>
            )}
          </div>
        </div>
      ) : (
      /* Content - Edit form view */
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Titel */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink"
          />
        </div>

        {/* Närvarolista */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Närvarolista</label>
          <div className="space-y-1">
            {presentAttendees.map((attendee, index) => (
              <label key={index} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={attendee.present}
                  onChange={() => toggleAttendeePresent(index)}
                  className="rounded text-leadme-pink focus:ring-leadme-pink"
                />
                <span className={attendee.present ? '' : 'text-gray-400 line-through'}>
                  {attendee.name}
                </span>
                <span className="text-gray-400 text-xs">({getRoleLabel(attendee.role)})</span>
              </label>
            ))}
          </div>
        </div>

        {/* Inledning */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Inledning (valfritt)</label>
          <textarea
            value={introduction}
            onChange={(e) => setIntroduction(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border rounded-lg resize-none focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink"
            placeholder="Kort inledning om mötet..."
          />
        </div>

        {/* Delade anteckningar att inkludera — grupperade per rum */}
        {availableNotes.length > 0 && (() => {
          const byRoom = {};
          availableNotes.forEach(note => {
            const key = note.room?.trim() || '__generellt__';
            if (!byRoom[key]) byRoom[key] = [];
            byRoom[key].push(note);
          });
          const roomKeys = Object.keys(byRoom).sort((a, b) => {
            if (a === '__generellt__') return 1;
            if (b === '__generellt__') return -1;
            return a.localeCompare(b, 'sv');
          });

          return (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lägg till från delade anteckningar
              </label>
              <div className="max-h-60 overflow-y-auto border rounded-lg p-2 bg-gray-50 space-y-3">
                {roomKeys.map(roomKey => (
                  <div key={roomKey}>
                    <div className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2 py-1 rounded mb-1">
                      {roomKey === '__generellt__' ? 'Generella punkter' : roomKey}
                    </div>
                    <div className="space-y-1">
                      {byRoom[roomKey].map(note => (
                        <div
                          key={note._id}
                          className={`flex items-start gap-2 p-2 rounded text-sm
                            ${selectedNoteIds.includes(note._id)
                              ? 'bg-blue-100 opacity-50'
                              : note.isActionPoint
                                ? 'bg-pink-50 hover:bg-pink-100 cursor-pointer border-l-2 border-leadme-pink'
                                : 'bg-white hover:bg-gray-100 cursor-pointer'
                            }`}
                          onClick={() => !selectedNoteIds.includes(note._id) && addSectionFromNote(note)}
                        >
                          <PlusIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
                          <div className="flex-1 min-w-0">
                            <p className="line-clamp-2 text-gray-700">{note.content}</p>
                            {note.isActionPoint && (
                              <span className="inline-flex items-center gap-1 mt-1 text-xs text-leadme-pink">
                                <ClipboardCheckIcon className="w-3 h-3" />
                                Actionpunkt
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Sektioner */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">Punkter</label>
            <button
              onClick={addEmptySection}
              className="flex items-center gap-1 text-sm text-leadme-pink hover:text-pink-600"
            >
              <PlusIcon className="w-4 h-4" />
              Lägg till punkt
            </button>
          </div>

          <div className="space-y-3">
            {sections.map((section, index) => (
              <div key={section._id} className={`border rounded-lg p-3 ${section.isActionPoint ? 'border-leadme-pink bg-pink-50/30' : ''}`}>
                <div className="flex items-start gap-2 mb-2">
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => moveSection(index, -1)}
                      disabled={index === 0}
                      className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <ChevronUpIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => moveSection(index, 1)}
                      disabled={index === sections.length - 1}
                      className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <ChevronDownIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={section.room || ''}
                    onChange={(e) => updateSection(section._id, 'room', e.target.value)}
                    className="w-24 px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400"
                    placeholder="Rum..."
                  />
                  <input
                    type="text"
                    value={section.title}
                    onChange={(e) => updateSection(section._id, 'title', e.target.value)}
                    className="flex-1 px-2 py-1 text-sm font-medium border rounded focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink"
                    placeholder="Rubrik (valfritt)"
                  />
                  {/* Actionpunkt-toggle */}
                  <button
                    onClick={() => toggleActionPoint(section._id)}
                    className={`p-1 rounded transition-colors ${
                      section.isActionPoint
                        ? 'text-leadme-pink bg-leadme-pink/10'
                        : 'text-gray-400 hover:text-leadme-pink hover:bg-leadme-pink/10'
                    }`}
                    title={section.isActionPoint ? 'Ta bort som actionpunkt' : 'Gör till actionpunkt'}
                  >
                    <ClipboardCheckIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => removeSection(section._id)}
                    className="p-1 text-gray-400 hover:text-red-600"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Actionpunkt-detaljer */}
                {section.isActionPoint && (
                  <div className="flex flex-wrap items-center gap-2 mb-2 ml-6">
                    <UserIcon className="w-4 h-4 text-gray-400" />
                    <select
                      value={getAssigneeKey(section.assignee)}
                      onChange={(e) => {
                        const selectedAssignee = getAvailableAssignees().find(a => getAssigneeKey(a) === e.target.value);
                        setAssignee(section._id, selectedAssignee || null);
                      }}
                      className="text-sm border rounded px-2 py-1 focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink"
                    >
                      <option value="">Välj ansvarig...</option>
                      {getAvailableAssignees().map((a, idx) => (
                        <option key={getAssigneeKey(a) || `attendee-${idx}`} value={getAssigneeKey(a)}>
                          {a.name} ({getRoleLabel(a.role)})
                        </option>
                      ))}
                    </select>
                    {section.assignee && (
                      <span className="text-xs text-leadme-pink bg-leadme-pink/10 px-2 py-0.5 rounded">
                        {getRoleLabel(section.assignee.role)}
                      </span>
                    )}
                    <div className="flex items-center gap-1 ml-2">
                      <CalendarIcon className="w-4 h-4 text-gray-400" />
                      <input
                        type="date"
                        value={section.dueDate ? new Date(section.dueDate).toISOString().split('T')[0] : ''}
                        onChange={(e) => updateSection(section._id, 'dueDate', e.target.value || null)}
                        className="text-sm border rounded px-2 py-1 focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink"
                      />
                    </div>
                  </div>
                )}

                <textarea
                  value={section.content}
                  onChange={(e) => updateSection(section._id, 'content', e.target.value)}
                  rows={3}
                  className="w-full px-2 py-1 text-sm border rounded resize-none focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink"
                  placeholder="Innehåll..."
                />
              </div>
            ))}

            {sections.length === 0 && (
              <div className="text-center py-4 text-gray-500 text-sm border rounded-lg bg-gray-50">
                Inga punkter ännu. Lägg till från anteckningar eller skapa en ny punkt.
              </div>
            )}
          </div>
        </div>

        {/* Avslutning */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Avslutning (valfritt)</label>
          <textarea
            value={conclusion}
            onChange={(e) => setConclusion(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border rounded-lg resize-none focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink"
            placeholder="Sammanfattning eller nästa steg..."
          />
        </div>
      </div>
      )}

        {/* Footer */}
        <div className="px-4 py-3 border-t flex justify-between items-center">
          <div>
            {protocol && canWrite && (
              <button
                onClick={handleDelete}
                disabled={saving}
                className="px-2.5 py-1.5 sm:px-3 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 flex items-center gap-1"
              >
                <TrashIcon className="w-4 h-4" />
                Ta bort
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Utkast: Spara + Publicera */}
            {protocol?.status === 'draft' && canWrite && (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-2.5 py-1.5 sm:px-3 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-1"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {saving ? 'Sparar...' : 'Spara'}
                </button>
                <button
                  onClick={handleMarkSent}
                  disabled={saving}
                  className="px-2.5 py-1.5 sm:px-3 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                  title="Publicerar protokollet så alla parter kan se det"
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                  Publicera
                </button>
              </>
            )}
            {/* Finalized (bakåtkompatibilitet): Publicera */}
            {protocol?.status === 'finalized' && canWrite && (
              <button
                onClick={handleMarkSent}
                disabled={saving}
                className="px-2.5 py-1.5 sm:px-3 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                title="Publicerar protokollet så alla parter kan se det"
              >
                <PaperAirplaneIcon className="w-4 h-4" />
                Publicera
              </button>
            )}
            {/* Nytt protokoll: Skapa */}
            {!protocol && canWrite && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-2.5 py-1.5 sm:px-3 text-sm font-medium bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink/90 transition-colors disabled:opacity-50 flex items-center gap-1"
              >
                <PlusIcon className="w-4 h-4" />
                {saving ? 'Skapar...' : 'Skapa protokoll'}
              </button>
            )}
          </div>
        </div>
      </div>

      <ProtocolRecipientModal
        isOpen={showRecipientModal}
        onClose={() => setShowRecipientModal(false)}
        onConfirm={handlePublishWithRecipients}
        attendees={meeting?.attendees || []}
        projectMembers={projectMembers}
        saving={saving}
        protocolTitle={protocol?.title || ''}
      />
    </div>
  );
}

export default ProtocolBuilder;
